const DATAEXPLORER_TABS_STORAGE_KEY = 'wingman:dataexplorer:tabs:v1';
const DATAEXPLORER_TABS_SESSION_KEY = 'wingman:dataexplorer:tabs:tab-id:v1';
const DATAEXPLORER_TABS_EVENT_NAME = 'wingman:dataexplorer-tabs-changed';
const DATAEXPLORER_TABS_HEARTBEAT_MS = 15000;
const DATAEXPLORER_TABS_STALE_MS = 90000;
const DATAEXPLORER_LOCATION_EVENT = 'wingman:dataexplorer-location-change';
const DATAEXPLORER_TABS_LABEL_ID = 'exampleSwanbotModalLabel';
const DATAEXPLORER_TABS_BADGE_SELECTOR = '[data-dataexplorer-tabs-badge="true"]';
const DATAEXPLORER_TABS_GRAPH_PENDING_BADGE_SELECTOR = '[data-dataexplorer-tabs-graph-pending="true"]';
const DATAEXPLORER_GRAPH_PENDING_BADGE_LABEL = 'Graph Data Request Pending';

let dataExplorerTabsInitialized = false;
let historyHooksInstalled = false;
let currentTabTracked = false;
let currentTabId = '';
let heartbeatId = null;
let lastPublishedSignature = '';
let graphDataPendingBadgeVisible = false;
let badgeObserver = null;

function isDataExplorerPath(pathname = window.location.pathname) {
  return typeof pathname === 'string' && pathname.startsWith('/dataexplorer');
}

function getCurrentTabId() {
  if (currentTabId) {
    return currentTabId;
  }

  let tabId = window.sessionStorage.getItem(DATAEXPLORER_TABS_SESSION_KEY);

  if (!tabId) {
    tabId = window.crypto && typeof window.crypto.randomUUID === 'function'
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    window.sessionStorage.setItem(DATAEXPLORER_TABS_SESSION_KEY, tabId);
  }

  currentTabId = tabId;
  return currentTabId;
}

function readTabsRegistry() {
  try {
    const registryRaw = window.localStorage.getItem(DATAEXPLORER_TABS_STORAGE_KEY);

    if (!registryRaw) {
      return {};
    }

    const parsedRegistry = JSON.parse(registryRaw);
    return parsedRegistry && typeof parsedRegistry === 'object' ? parsedRegistry : {};
  } catch {
    return {};
  }
}

function writeTabsRegistry(registry) {
  window.localStorage.setItem(DATAEXPLORER_TABS_STORAGE_KEY, JSON.stringify(registry));
}

function pruneTabsRegistry(registry) {
  const now = Date.now();
  const cleanedRegistry = {};

  Object.entries(registry).forEach(function ([tabId, tabInfo]) {
    if (!tabInfo || typeof tabInfo !== 'object') {
      return;
    }

    if (!isDataExplorerPath(tabInfo.path)) {
      return;
    }

    const timestamp = Number(tabInfo.timestamp || 0);

    if (!timestamp || now - timestamp >= DATAEXPLORER_TABS_STALE_MS) {
      return;
    }

    cleanedRegistry[tabId] = {
      path: tabInfo.path,
      url: typeof tabInfo.url === 'string' ? tabInfo.url : tabInfo.path,
      timestamp
    };
  });

  return cleanedRegistry;
}

function getRegistrySignature(registry) {
  return JSON.stringify(Object.keys(registry).sort().map(function (tabId) {
    const tabInfo = registry[tabId];
    return [tabId, tabInfo.path, tabInfo.url];
  }));
}

function publishTabsState(registry, force = false) {
  const nextSignature = getRegistrySignature(registry);

  if (!force && nextSignature === lastPublishedSignature) {
    return;
  }

  lastPublishedSignature = nextSignature;
  window.dispatchEvent(new CustomEvent(DATAEXPLORER_TABS_EVENT_NAME, {
    detail: {
      count: Object.keys(registry).length,
      registry,
      tabIds: Object.keys(registry)
    }
  }));
}

function formatTabCountLabel(count) {
  return `${count} active session${count === 1 ? '' : 's'}`;
}

function ensureTabCountBadge(labelElement) {
  let badgeElement = labelElement.querySelector(DATAEXPLORER_TABS_BADGE_SELECTOR);

  if (badgeElement) {
    return badgeElement;
  }

  badgeElement = document.createElement('span');
  badgeElement.setAttribute('data-dataexplorer-tabs-badge', 'true');
  badgeElement.className = 'badge rounded-pill';
  badgeElement.style.display = 'inline-flex';
  badgeElement.style.alignItems = 'center';
  badgeElement.style.marginLeft = '10px';
  badgeElement.style.padding = '4px 10px';
  badgeElement.style.fontSize = '12px';
  badgeElement.style.fontWeight = '600';
  badgeElement.style.lineHeight = '1';
  badgeElement.style.verticalAlign = 'middle';
  badgeElement.style.backgroundColor = '#f1f3f5';
  badgeElement.style.color = '#495057';
  badgeElement.style.border = '1px solid #ced4da';

  labelElement.appendChild(badgeElement);
  return badgeElement;
}

function ensureGraphDataPendingBadge(labelElement) {
  let badgeElement = labelElement.querySelector(DATAEXPLORER_TABS_GRAPH_PENDING_BADGE_SELECTOR);

  if (badgeElement) {
    return badgeElement;
  }

  badgeElement = document.createElement('span');
  badgeElement.setAttribute('data-dataexplorer-tabs-graph-pending', 'true');
  badgeElement.className = 'badge rounded-pill';
  badgeElement.style.display = 'inline-flex';
  badgeElement.style.alignItems = 'center';
  badgeElement.style.marginLeft = '8px';
  badgeElement.style.padding = '4px 10px';
  badgeElement.style.fontSize = '12px';
  badgeElement.style.fontWeight = '600';
  badgeElement.style.lineHeight = '1';
  badgeElement.style.verticalAlign = 'middle';
  badgeElement.style.backgroundColor = '#fff3bf';
  badgeElement.style.color = '#5f3b00';
  badgeElement.style.border = '1px solid #ffd166';

  labelElement.appendChild(badgeElement);
  return badgeElement;
}

function renderTabCountBadges(count = getDataExplorerOpenTabCount()) {
  document.querySelectorAll(`#${DATAEXPLORER_TABS_LABEL_ID}`).forEach(function (labelElement) {
    const badgeElement = ensureTabCountBadge(labelElement);
    const badgeLabel = formatTabCountLabel(count);
    const pendingBadgeElement = labelElement.querySelector(DATAEXPLORER_TABS_GRAPH_PENDING_BADGE_SELECTOR);

    badgeElement.textContent = badgeLabel;
    badgeElement.setAttribute('aria-label', badgeLabel);
    badgeElement.setAttribute('title', badgeLabel);

    if (!graphDataPendingBadgeVisible) {
      if (pendingBadgeElement) {
        pendingBadgeElement.remove();
      }

      return;
    }

    const graphPendingBadgeElement = ensureGraphDataPendingBadge(labelElement);
    graphPendingBadgeElement.textContent = DATAEXPLORER_GRAPH_PENDING_BADGE_LABEL;
    graphPendingBadgeElement.setAttribute('aria-label', DATAEXPLORER_GRAPH_PENDING_BADGE_LABEL);
    graphPendingBadgeElement.setAttribute('title', DATAEXPLORER_GRAPH_PENDING_BADGE_LABEL);
  });
}

function initDataExplorerTabBadges() {
  if (!badgeObserver) {
    badgeObserver = new MutationObserver(function () {
      const shouldRender = Array.from(arguments[0] || []).some(function (mutation) {
        const changedNodes = [...mutation.addedNodes, ...mutation.removedNodes];

        return changedNodes.some(function (node) {
          if (!(node instanceof Element)) {
            return false;
          }

          return node.id === DATAEXPLORER_TABS_LABEL_ID || !!node.querySelector(`#${DATAEXPLORER_TABS_LABEL_ID}`);
        });
      });

      if (shouldRender) {
        renderTabCountBadges();
      }
    });

    if (document.body) {
      badgeObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    window.addEventListener(DATAEXPLORER_TABS_EVENT_NAME, function (event) {
      const count = event && event.detail ? event.detail.count : getDataExplorerOpenTabCount();
      renderTabCountBadges(count);
    });
  }

  renderTabCountBadges();
}

function syncTabsRegistry(options = {}) {
  const registry = pruneTabsRegistry(readTabsRegistry());
  const shouldTrackCurrentTab = options.trackCurrentTab === true;
  const shouldRemoveCurrentTab = options.removeCurrentTab === true;
  const tabId = getCurrentTabId();
  let changed = false;

  if (shouldRemoveCurrentTab) {
    if (registry[tabId]) {
      delete registry[tabId];
      changed = true;
    }
  } else if (shouldTrackCurrentTab) {
    const nextRecord = {
      path: window.location.pathname,
      url: window.location.pathname + window.location.search,
      timestamp: Date.now()
    };
    const previousRecord = registry[tabId];

    if (
      !previousRecord ||
      previousRecord.path !== nextRecord.path ||
      previousRecord.url !== nextRecord.url ||
      previousRecord.timestamp !== nextRecord.timestamp
    ) {
      registry[tabId] = nextRecord;
      changed = true;
    }
  }

  if (changed || options.forceWrite === true) {
    writeTabsRegistry(registry);
  }

  publishTabsState(registry, options.forcePublish === true || changed);

  return {
    count: Object.keys(registry).length,
    registry
  };
}

function clearHeartbeat() {
  if (heartbeatId) {
    window.clearInterval(heartbeatId);
    heartbeatId = null;
  }
}

function startTrackingCurrentTab() {
  if (currentTabTracked) {
    syncTabsRegistry({ trackCurrentTab: true, forcePublish: true });
    return;
  }

  currentTabTracked = true;
  syncTabsRegistry({ trackCurrentTab: true, forcePublish: true });

  clearHeartbeat();
  heartbeatId = window.setInterval(function () {
    syncTabsRegistry({ trackCurrentTab: true });
  }, DATAEXPLORER_TABS_HEARTBEAT_MS);
}

function stopTrackingCurrentTab() {
  if (!currentTabTracked) {
    syncTabsRegistry({ forcePublish: true });
    return;
  }

  currentTabTracked = false;
  clearHeartbeat();
  syncTabsRegistry({ removeCurrentTab: true, forcePublish: true });
}

function handleLocationChange() {
  if (isDataExplorerPath()) {
    startTrackingCurrentTab();
    return;
  }

  stopTrackingCurrentTab();
}

function dispatchLocationChange() {
  window.dispatchEvent(new Event(DATAEXPLORER_LOCATION_EVENT));
}

function installHistoryHooks() {
  if (historyHooksInstalled) {
    return;
  }

  historyHooksInstalled = true;

  const wrapHistoryMethod = function (methodName) {
    const originalMethod = window.history[methodName];

    if (typeof originalMethod !== 'function') {
      return;
    }

    window.history[methodName] = function () {
      const result = originalMethod.apply(this, arguments);
      dispatchLocationChange();
      return result;
    };
  };

  wrapHistoryMethod('pushState');
  wrapHistoryMethod('replaceState');

  window.addEventListener('popstate', dispatchLocationChange);
  window.addEventListener('hashchange', dispatchLocationChange);
  window.addEventListener(DATAEXPLORER_LOCATION_EVENT, handleLocationChange);
}

export function getDataExplorerOpenTabsSnapshot() {
  return syncTabsRegistry({ trackCurrentTab: currentTabTracked }).registry;
}

export function getDataExplorerOpenTabCount() {
  return syncTabsRegistry({ trackCurrentTab: currentTabTracked }).count;
}

export function isCurrentDataExplorerTabTracked() {
  return currentTabTracked;
}

export function initDataExplorerTabs() {
  if (dataExplorerTabsInitialized) {
    handleLocationChange();
    initDataExplorerTabBadges();
    return;
  }

  dataExplorerTabsInitialized = true;
  installHistoryHooks();
  initDataExplorerTabBadges();

  window.addEventListener('storage', function (event) {
    if (event.key === DATAEXPLORER_TABS_STORAGE_KEY) {
      syncTabsRegistry({ trackCurrentTab: currentTabTracked, forcePublish: true });
    }
  });

  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') {
      syncTabsRegistry({ trackCurrentTab: currentTabTracked, forcePublish: true });
    }
  });

  window.addEventListener('pageshow', function () {
    handleLocationChange();
  });

  window.addEventListener('pagehide', function () {
    stopTrackingCurrentTab();
  });

  window.addEventListener('beforeunload', function () {
    stopTrackingCurrentTab();
  });

  handleLocationChange();
}

export function setDataExplorerGraphDataPendingBadge(isVisible) {
  graphDataPendingBadgeVisible = isVisible === true;
  renderTabCountBadges();
}

export {
  DATAEXPLORER_TABS_EVENT_NAME
};